import { type DateTimeFormatOptions } from 'luxon';
import { levels } from 'pino';
import { type PrettyOptions, type PrettyStream } from 'pino-pretty';
import { type Required } from 'utility-types';

export type ParsedArgs = Partial<{
  readonly context: LogContext;
  readonly error: Error;
  readonly message: string;
}>;

export type LogContext = Record<string, unknown>;

/**
 * Represents the various IDs that a given logger strategy, {@link IStrategy}, can be identified by.
 * These IDs are used to uniquely identify a given strategy, {@link IStrategy}, configured for a
 * logger instance, {@link ILogger}, and help prevent the configuration of logger instances,
 * {@link ILogger}, with duplicate strategies.
 */
export enum StrategyId {
  Console = 'Console',
  DataDogRum = 'DataDogRum',
  Pino = 'Pino',
}

/**
 * Defines the various strategy behaviors for emitting logs that a logger can encompass.
 *
 * The {@link LoggerStrategy} differs from the {@link LoggerMethod} in the sense that a
 * {@link LoggerMethod} must always be chosen/exist, whereas a {@link LoggerStrategy} is used to
 * encompass additional, optional logging behavior on top of the method, {@link LoggerMethod}.
 */

export enum LogLevel {
  Debug = 'debug',
  Error = 'error',
  Info = 'info',
  Trace = 'trace',
  Warn = 'warn',
}

export const isLogLevel = (value: unknown): value is LogLevel =>
  Object.values(LogLevel).includes(value as LogLevel);

export const inferLogLevel = (value: unknown): LogLevel | undefined => {
  if (typeof value === 'number') {
    return inferLogLevel(levels.labels[value]);
  } else if (typeof value === 'string' && isLogLevel(value.toLowerCase())) {
    return value.toLowerCase() as LogLevel;
  }
  return undefined;
};

type ValueOrIndexedLevelValue<T> = Partial<Record<LogLevel, T>> | T;

/**
 * Represents the general form that a given logger strategy must conform to.
 *
 * Logger strategies are configurable entities that are provided to logger instances that are
 * responsible for handling the emission of log messages, {@link string}, log context,
 * {@link types.LogContext}, and sometimes errors, {@link types.Error}, through various
 * mediums.
 */
export interface IStrategy {
  /**
   * Emits the provided message, {@link string}, context, {@link LogContext}, and in some cases,
   * error, {@link Error}, to the medium that the {@link IStrategy} is responsible for managing.
   *
   * @param {LogLevel} level
   *   The level associated with the original log method on the {@link Logger} instance that was
   *   called.
   * @param {ParsedArg} params
   *   Parameters that optionally include the message, {@link string}, context, {@link LogContext},
   *   and in some cases, error, {@link Error}.
   */
  logWithStrategy(logger: ILoggerConfiguration, level: LogLevel, params: ParsedArgs): void;
  /**
   * The {@link StrategyId} that uniquely identifies the behavior of the {@link IStrategy}.
   *
   * @see {@link StrategyId}
   */
  readonly strategyId: StrategyId;
}

/**
 * Represents the configuration options that can be provided when a new logger instance is created.
 * These configuration options are used to define the behavior of the logger instance, including
 * the way in which logs are emitted.
 */
export interface LoggerOptions {
  /**
   * The name of the application or package that is using the logger.  This value will be included
   * in the default context and/or message that is emitted with each call to the logger.
   */
  readonly appName?: string;
  /**
   * The version of the application or package that is using the logger. This value will be included
   * in the default context and/or message that is emitted with each call to the logger.
   */
  readonly appVersion?: string;
  /**
   * Sets the tags that will be assigned to logs coming out of this logger, as read by DataDog.
   */
  readonly ddtags?: string;
  /**
   * A string that represents the current environment that the logger is operating in.  This value
   * will be included in the default context and/or message that is emitted with each call to the
   * logger.
   *
   * If not provided, this value will default based on 'process.env.NODE_ENV'.
   */
  readonly environmentName?: string;
  readonly globalContext?: LogContext;
  /**
   * Whether or not the logger instance is enabled entirely.  This can be used to disable the logger
   * from emitting any information in certain environments, such as in a test environment.
   *
   * @default true
   */
  readonly isEnabled?: boolean;
  /**
   * The log level, {@link LogLevel}, that the logger should be configured for, which determines
   * the minimum severity level that emitted logs should meet before being emitted.
   *
   * @default {LogLevel.Trace}
   */
  readonly level?: LogLevel;
  readonly messageFormatter?: ValueOrIndexedLevelValue<(message: string) => string>;
  readonly messagePrefix?: ValueOrIndexedLevelValue<string>;
  readonly name?: string;
  /**
   * Whether or not log messages and context should be "pretty-printed" when they are outputted.
   *
   * In server contexts, when the "pino-pretty" package is supported by the current runtime
   * environment and the logger is configured to use the method {@link LoggerMethod.Pino},
   * this configuration parameter determines whether or not the "pino-pretty" package is imported
   * and used in conjunction with the underlying "pino" logger.
   *
   * In non-server contexts, or when the "pino-pretty" package is not supported by the current
   * runtime environment, OR when the logger is configured to use the method
   * {@link LoggerMethod.Console}, this configuration parameter determines whether or not manual
   * color styles are applied to the output that is emitted to the console.
   *
   * @default true
   */
  readonly pretty?: boolean;
  /**
   * Optionally defines the various destinations that logs can be emitted to outside of the
   * traditional behavior.
   *
   * @see {LoggerStrategy}
   */
  readonly strategies?: IStrategy[];
  /**
   * The time formatting token {@link string} or options {@link DateTimeFormatOptions} that should
   * be used to format the time in which the log was emitted when logs are issued via the
   * traditional console object.
   *
   * @default 'LLL dd yyyy HH:mm:ss'
   */
  readonly timeFormat?: DateTimeFormatOptions | string;
}

export type PrettyPackage = (opts: PrettyOptions) => PrettyStream;

/**
 * Defines the form of a logger class that is capable of being used to instantiate logger instances
 * via the {@link LoggerConfig} class.  This class must extend {@link Logger} and have a constructor
 * that accepts a name, {@link string}, and options, {@link O}.
 */
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export type LoggerClass<T = unknown, O extends LoggerOptions = any> = new (options: O) => T;

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export type InferredLoggerInstance<C> = C extends LoggerClass<infer T, any> ? T : never;

export type InferredLoggerOptions<T> =
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  T extends LoggerClass<any, infer O extends LoggerOptions> ? O : never;

export interface ProcessedLogContext extends Required<
  Pick<LoggerOptions, 'appName' | 'appVersion' | 'environmentName'>
> {
  [key: string]: unknown;
  readonly name: string;
}

interface ILoggerErrorMethod {
  (error: unknown, message: string, context?: LogContext): void;
  (errorOrMessage: unknown, context?: LogContext): void;
  (context: LogContext): void;
}

interface ILoggerMethod {
  (message: string, context?: LogContext): void;
  (context: LogContext): void;
}

export interface ILoggerPublicApi {
  readonly debug: ILoggerMethod;
  readonly error: ILoggerErrorMethod;
  readonly info: ILoggerMethod;
  readonly trace: ILoggerMethod;
  readonly warn: ILoggerErrorMethod;
}

export interface ILoggerConfiguration extends Pick<
  ProcessedLogContext,
  'appName' | 'appVersion' | 'environmentName' | 'name'
> {
  readonly context: LogContext;
  readonly formatMessage: (level: LogLevel, message: string) => string;
  readonly isEnabled: boolean;
  readonly level: LogLevel;
  readonly pretty: boolean;
  readonly timeFormat: DateTimeFormatOptions | string;
}

export interface ILogger extends ILoggerConfiguration, ILoggerPublicApi {
  child(name: string): ILogger;
}
